<script setup lang="ts">
import BandProfileCountryFlag from '~/components/band/profile/BandProfileCountryFlag.vue'
import BandProfileBanner from '~/components/band/profile/banner.vue'
import BandProfileBase from '~/components/band/profile/base.vue'
import BandProfileEditConfirm from '~/components/band/profile/edit/confirm.vue'
import BandProfileNavigation from '~/components/band/profile/navigation.vue'
import BandProfileTrack from '~/components/band/profile/tracks.vue'
import PageFooter from '~/components/shared/footer/index.vue'
import ForceArtist from '~/components/shared/profile/ForceArtist.vue'
import DirectionalSwipe from '~/components/transitions/directionalSwipe.vue'
import { Base400Gray, Sm400Gray, Sm500Gray } from '~/components/typography'
import { BtnOrange } from '~/components/ui/Buttons'
import VText from '~/components/ui/VText.vue'

import { useGetMediaUrl } from '~/composables/useGetMediaUrl'
import { useGetProfilePicture } from '~/composables/useGetProfilePicture'
import { useIsUserOwnerOfBand } from '~/composables/useIsUserOwnerOfBand'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'
import { useSortTagsByTranslatedName } from '~/composables/useTagHelpers'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useTagStore } from '~/stores/tag'

import { addHttps } from '~/utils/addHttps'
import { isString } from '~/utils/type-guards'

import { terms } from '~/assets/helpers/bandProfileSlugNoIndexTerms'

import type Tag from '~/entities/tag'
import type { Band } from '~/types/band'
import type { Locale } from '~/types/locale'
import type { SwipeDirection } from '~/types/SwipeDirection'
import type Track from '~/types/track'

const { getMediaUrl } = useGetMediaUrl()
const { t, locale } = useI18n()
const route = useRoute()
const { coreFetch } = useProvideCoreFetch()
const miscResizeStore = useMiscResizeStore()
const tagStore = useTagStore()

const slug =
  typeof route.params.slug === 'string'
    ? route.params.slug
    : route.params.slug[0]
const { userIsOwner } = useIsUserOwnerOfBand(slug)
const { timestamp } = useGetServerSyncedTimestamp('bandProfileAvatar')
const { getProfilePictureUrl } = useGetProfilePicture()
const sortTagsByTranslatedName = useSortTagsByTranslatedName()

// data
const navigationIndex = ref(Number(route.query.tab ?? 0))
const navigationSwipeDirection = ref<SwipeDirection>('right')

const { data: band } = await useAsyncData<Band>(() =>
  coreFetch.$get(`/band/slug/${route.params.slug}/`),
)
const { data: trackSet } = useAsyncData<Track[]>(
  () => coreFetch.$get(`/track/track/bulk/?slug=${route.params.slug}`),
  {
    default: () => [],
  },
)

callOnce('FETCH_TAGS', tagStore.FETCH)

// computed
const tagPicture = computed<string>(() => {
  if (band.value?.has_profile_picture) {
    return (
      getProfilePictureUrl({
        target: 'profile_picture',
        size: '400_400',
        slug: band.value?.slug,
        kind: 'band',
      }) + `?${timestamp.value}`
    )
  } else {
    return 'https://groover.co/media/groover-home.jpg'
  }
})

const LOCALE = computed<Locale>(() => {
  return locale.value as Locale
})

const mediaLinks = computed<{ link: string; key: string }[]>(() => {
  return Object.entries(band.value?.media_links ?? {}).reduce(
    (accumulator, [key, value]) => {
      if (!value || typeof value === 'number' || !value.length)
        return accumulator

      const link = addHttps(value ?? '')
      if (link.length) accumulator.push({ link, key })

      return accumulator
    },
    [] as { link: string; key: string }[],
  )
})

const mediaLinkImgPaths = computed(() => {
  const imgPaths = [] as string[]

  mediaLinks.value.forEach((item) => {
    const path = getMediaUrl(`${item.key}.svg`)
    imgPaths.push(path ?? '')
  })

  return imgPaths
})

const picture = computed<string>(() => {
  const hasParam = Number(route.query.force_picture) === 1
  const hasPicture = band.value?.has_profile_picture || hasParam

  return (
    getProfilePictureUrl({
      target: 'profile_picture',
      size: '400_400',
      slug: hasPicture ? band.value?.slug : undefined,
      kind: 'band',
    }) + `?${timestamp.value}`
  )
})

const BAND_COUNTRIES = computed<Tag[]>(() => {
  return tagStore
    .GET_TAGS_FROM_IDS(band.value?.tags?.identity.country ?? [])
    .sort((a: Tag, b: Tag) => sortTagsByTranslatedName('country', a, b))
})

const BAND_SUBGENRES = computed<string>(() => {
  return (
    tagStore.GET_TAGS_FROM_IDS(
      band.value?.tags?.identity.subgenre ?? [],
    ) as Tag[]
  )
    .map((e) => t(`tags.subgenre.${e.name}`))
    .join(', ')
})

const BAND_MISC = computed<string>(() => {
  return [
    ...(
      tagStore.GET_TAGS_FROM_IDS(band.value?.tags?.identity.mood ?? []) as Tag[]
    ).map((e) => t(`tags.mood.${e.name}`)),
    ...(
      tagStore.GET_TAGS_FROM_IDS(
        band.value?.tags?.identity.artist_kind ?? [],
      ) as Tag[]
    ).map((e) => t(`tags.artist_kind.${e.name}`)),
  ].join(', ')
})

const selectedComponent = computed(() => {
  return navigationIndex.value === 0 ? BandProfileBase : BandProfileTrack
})

const displaySocialLinks = computed<boolean>(() => {
  return (
    !!band.value?.media_links && Object.keys(band.value?.media_links).length > 0
  )
})

// page head meta data
const { name: bandName } = band.value ?? { name: '' }
const title = t('metaTags.bandProfileSlug.title', {
  bandName,
})
const description = t('metaTags.bandProfileSlug.description', {
  bandName,
})
const seoGenreTag = t(
  `tags.subgenre.${
    tagStore.GET_TAG_FROM_ID(band.value?.tags?.identity.subgenre?.[0] ?? 0)
      ?.name
  }`,
)

const {
  public: { ENV_NAME },
} = useRuntimeConfig()

/**
 * This is to avoid duplicate indexing on google
 */
const slugIsNotId =
  isString(route.params.slug) && route.params.slug.match(/\D/gi)

useHead({
  script: [
    {
      src: 'https://www.youtube.com/iframe_api',
      async: false,
      defer: false,
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'MusicGroup',
        name: band.value?.name,
        genre: seoGenreTag,
        image: tagPicture.value,
        description: band.value
          ? band.value[
              `biography_${LOCALE.value === 'de' ? 'en' : LOCALE.value}` as const
            ]
          : '',
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        name: band.value?.name,
      }),
    },
  ],
  title,
  meta: [
    {
      name: 'description',
      key: 'description',
      content: description,
    },
    {
      key: 'og:title',
      property: 'og:title',
      content: title,
    },
    {
      key: 'og:description',
      property: 'og:description',
      content: description,
    },
    {
      key: 'og:image',
      property: 'og:image',
      content: tagPicture.value,
    },
    {
      key: 'robots',
      name: 'robots',
      content:
        slugIsNotId &&
        ENV_NAME === 'prod' &&
        !band.value?.archived &&
        !band.value?.deleted &&
        !band.value?.disable_indexing &&
        !!band.value?.first_submission_date?.length &&
        !terms.some((term) => band.value?.slug.includes(term))
          ? 'index, follow'
          : 'noindex, follow',
    },
  ],
})

// methods
function handleSwipeDirection(value: number): void {
  if (navigationIndex.value > value) navigationSwipeDirection.value = 'right'
  else navigationSwipeDirection.value = 'left'

  nextTick(() => {
    navigationIndex.value = value
  })
}

function formatUrl(url: string): string {
  url = url ?? ''
  if (url.match(/https?:\/\//) === null) url = 'https://' + url

  return url ?? ''
}
</script>

<template>
  <div class="tw-overflow-x-hidden">
    <div
      v-if="band"
      id="ignoreFontUpscale"
      class="mainParentContainer ignoreFontUpscale"
    >
      <BandProfileEditConfirm />
      <div>
        <ForceArtist v-if="userIsOwner" :band="band" />
        <BandProfileBanner :band="band" :can-edit="userIsOwner" />
        <div
          class="tw-mb-8 tw-rounded-b tw-border tw-border-solid tw-border-gray-300"
        >
          <div
            class="tw-flex tw-flex-wrap tw-items-start tw-justify-center tw-bg-white tw-px-4 md:tw-flex-nowrap md:tw-justify-start md:tw-px-16"
          >
            <div
              class="roundedImageWrap imageWrap md:tw-mr-6"
              data-test-id="bandProfilePicture"
            >
              <NuxtLinkLocale
                v-if="userIsOwner"
                class="editPrompt"
                :to="`/band/profile/${band.slug}/edit/`"
              >
                <i class="fas fa-pen" />
              </NuxtLinkLocale>
              <img :src="picture" alt="" />
            </div>
            <div class="md:tw-mt-8 md:tw-w-full">
              <div
                class="tw-mb-4 tw-flex tw-justify-center md:tw-items-center md:tw-justify-between"
              >
                <VText cfg="sans/24/bold" data-test-id="bandProfileBandName">
                  {{ band.name }}
                </VText>

                <NuxtLinkLocale
                  v-if="userIsOwner && miscResizeStore.SCREEN_WIDTH >= 768"
                  :to="`/band/profile/${band.slug}/edit/`"
                >
                  <BtnOrange small>
                    {{ $t('common.edit') }}
                  </BtnOrange>
                </NuxtLinkLocale>
              </div>
              <div
                class="tw-mb-2xl tw-grid tw-grid-cols-1 tw-gap-sm tw-text-center md:tw-text-left"
              >
                <Sm500Gray>
                  {{ BAND_SUBGENRES }}
                </Sm500Gray>
                <Sm400Gray>
                  {{ BAND_MISC }}
                </Sm400Gray>
              </div>
              <div class="tw-flex">
                <div
                  v-for="{ id, name } in BAND_COUNTRIES"
                  :key="id"
                  class="tw-mr-10 tw-flex tw-items-center tw-justify-center md:tw-justify-start"
                >
                  <div class="tw-relative tw-mr-2 tw-w-12">
                    <BandProfileCountryFlag
                      class="tw-relative"
                      :country="name"
                    />
                  </div>
                  <Base400Gray>
                    {{ $t(`tags.country.${name}`) }}
                  </Base400Gray>
                </div>
              </div>
              <div
                v-if="displaySocialLinks || band.link"
                class="socialLinksDisplay"
              >
                <a
                  v-for="(elem, index) in mediaLinks"
                  :key="elem.key"
                  class="imageWrap"
                  :href="formatUrl(elem.link)"
                  target="_blank"
                >
                  <img :src="mediaLinkImgPaths[index]" alt="" />
                </a>
                <a
                  v-if="band.link"
                  :href="formatUrl(band.link)"
                  target="_blank"
                  class="tw-flex tw-items-center tw-justify-center"
                >
                  <i class="fas fa-link" />
                </a>
              </div>
              <div
                v-if="userIsOwner && miscResizeStore.SCREEN_WIDTH < 768"
                class="tw-mb-6 tw-flex tw-justify-center"
              >
                <NuxtLinkLocale :to="`/band/profile/${band.slug}/edit/`">
                  <BtnOrange data-test-id="bandProfileEditCTA" small>
                    {{ $t('common.edit') }}
                  </BtnOrange>
                </NuxtLinkLocale>
              </div>
            </div>
          </div>
          <div class="navWrap">
            <BandProfileNavigation
              :model-value="navigationIndex"
              @update:model-value="handleSwipeDirection"
            />
          </div>
          <div class="tw-p-8">
            <DirectionalSwipe :direction="navigationSwipeDirection">
              <component
                :is="selectedComponent"
                :key="navigationIndex"
                v-model="trackSet"
                :track-set="trackSet"
                :band="band"
              />
            </DirectionalSwipe>
          </div>
        </div>
      </div>
      <PageFooter class="tw-w-force-full-grid-system" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.socialLinksDisplay {
  @apply tw-my-8 tw-flex tw-flex-wrap tw-justify-center tw-gap-4;

  > .imageWrap {
    @apply tw-h-8 tw-w-8;
  }

  @screen md {
    @apply tw-justify-start;
  }
}

.roundedImageWrap {
  @apply tw-relative tw-mb-6 tw-h-48 tw-w-48 tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-border-4 tw-border-solid tw-border-white;

  margin-top: -96px;

  > .editPrompt {
    @apply tw-absolute tw-left-0 tw-top-0 tw-z-20 tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-black tw-bg-opacity-75 tw-text-white tw-opacity-100 tw-transition-opacity tw-duration-150 tw-ease-in-out;
  }

  @screen lg {
    > .editPrompt {
      @apply tw-opacity-0;
    }

    &:hover {
      > .editPrompt {
        @apply tw-opacity-100;
      }
    }
  }
}

.imageWrap {
  position: relative;

  > img {
    @apply tw-h-full tw-w-full tw-object-contain tw-object-center;
  }
}
</style>
